import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { EntityType, QueryFilter } from '@mariana/labos-client';
import type { ColumnDef } from '../config/viewsConfig';
import { useEntityList } from './useEntityList';
import type { UseEntityListResult } from './useEntityList';
import { filtersEqual } from '../utils/filtersEqual';

interface UseViewStateOptions {
  entityType: EntityType;
  allColumns: ColumnDef[];
  initialFilters?: QueryFilter[];
  defaultSort?: { by: string; order: 'asc' | 'desc' };
  viewId?: string;
  disabled?: boolean;
  extraFilters?: QueryFilter[];
}

export interface UseViewStateResult extends UseEntityListResult {
  visibleColumns: ColumnDef[];
  setVisibleColumns: (cols: ColumnDef[]) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  isDirty: boolean;
  allColumns: ColumnDef[];
}

function columnsMatchDefault(visible: ColumnDef[], all: ColumnDef[]): boolean {
  if (visible.length !== all.length) return false;
  for (let i = 0; i < visible.length; i++) {
    if (visible[i].key !== all[i].key) return false;
  }
  return true;
}

export function useViewState({
  entityType,
  allColumns,
  initialFilters = [],
  defaultSort,
  viewId,
  disabled = false,
  extraFilters,
}: UseViewStateOptions): UseViewStateResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleColumns, setVisibleColumnsState] = useState(allColumns);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const effectiveViewId = viewId ?? entityType;
  const allColumnsKey = JSON.stringify(allColumns.map(c => c.key));

  useEffect(() => {
    const colsParam = searchParams.get('cols');
    if (colsParam) {
      const keys = new Set(colsParam.split(','));
      const restored = allColumns.filter(c => keys.has(c.key));
      setVisibleColumnsState(restored.length > 0 ? restored : allColumns);
    } else {
      setVisibleColumnsState(allColumns);
    }
    setSearchQuery('');
    setDebouncedSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveViewId, allColumnsKey]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const setVisibleColumns = useCallback((cols: ColumnDef[]) => {
    setVisibleColumnsState(cols);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (columnsMatchDefault(cols, allColumns)) {
        next.delete('cols');
      } else {
        next.set('cols', cols.map(c => c.key).join(','));
      }
      return next;
    }, { replace: true });
  }, [allColumns, setSearchParams]);

  const entityList = useEntityList({
    entityType,
    columns: allColumns,
    initialFilters,
    defaultSort,
    viewId,
    disabled,
    search: debouncedSearch || undefined,
    extraFilters,
  });

  const defaultSortBy = defaultSort?.by ?? 'id';
  const defaultSortOrder = defaultSort?.order ?? 'desc';

  const isDirty = useMemo(() => {
    if (!filtersEqual(entityList.filters, initialFilters)) return true;
    if (entityList.sortBy !== defaultSortBy || entityList.sortOrder !== defaultSortOrder) return true;
    if (!columnsMatchDefault(visibleColumns, allColumns)) return true;
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityList.filters, initialFilters, entityList.sortBy, entityList.sortOrder, visibleColumns, allColumnsKey, defaultSortBy, defaultSortOrder]);

  return {
    ...entityList,
    visibleColumns,
    setVisibleColumns,
    searchQuery,
    setSearchQuery,
    isDirty,
    allColumns,
  };
}
